import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CAP, useSession } from "@/auth/session";
import { products } from "@/api/client";

import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Drawer } from "@/components/Drawer";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { StatGrid } from "@/components/StatGrid";
import {
  qk, useModels, useProductCompanies, useProducts, useSpaces, type ProductRow,
} from "@/modules/products/data";
import { ScopeAction, ScopeField } from "@/modules/products/ScopeBar";
import { SpecEditor } from "@/modules/products/SpecEditor";

/** Products — every catalogue kind, and what hangs off it.
 *
 *  A product IS a kind (D-92). Fabric's three-row `products` table is gone;
 *  `cluster_meter` became Cluster, `connected_cluster` became Connected
 *  Cluster, and Orbit Platform was retired because it was never hardware.
 *
 *  ROW CLICK OPENS A DRAWER. Dattatray: *"Click on each line item shall open
 *  the side drawer with more info and there is lot of info to be seen on that
 *  product."* The table can only carry the counts; the drawer carries what
 *  those counts are OF — which spaces, which models, which tools — without
 *  making a person leave the list to find out.
 *
 *  THE CATEGORY IS A COLUMN NO LONGER, AND A DROPDOWN GROUPING NOW.
 *  Dattatray said both things, a few hours apart, and both were right:
 *  *"A product sub group ... Not required"* and then *"I think product
 *  categories were useful! They make dropdown of product much easier to look
 *  at!"*
 *
 *  What changed is what the category is FOR. In this table it was noise —
 *  every row repeating "Control & display" tells you nothing the row does
 *  not. In `ProductSelect` it is the opposite: thirteen flat options is a
 *  list you read, five labelled groups is a list you scan. Same column, and
 *  it earns its place in one of the two.
 */
const COLS: DataCol<ProductRow>[] = [
  { key: "name", label: "Product", width: 190, render: (p) => p.name },
  { key: "kind", label: "Kind", width: 130, mono: true, render: (p) => p.kind },
  { key: "abbr", label: "Abbr", width: 70, mono: true, render: (p) => p.abbr },
  {
    key: "shape", label: "", width: 150,
    render: (p) => (
      <span style={{ display: "flex", gap: 4 }}>
        {p.is_asset && <Chip tone="ok">asset</Chip>}
        {p.is_connected && <Chip tone="info">connected</Chip>}
      </span>
    ),
  },
  {
    key: "models", label: "Models", width: 90,
    render: (p) => p.catalogue_versions,
  },
  { key: "spaces", label: "Spaces", width: 80, render: (p) => p.spaces },
  { key: "maps", label: "Tool maps", width: 90, render: (p) => p.permission_maps },
  { key: "files", label: "Ref files", width: 90, render: (p) => p.reference_files },
  { key: "tools", label: "Releases", width: 90, render: (p) => p.tool_releases },
  { key: "companies", label: "Companies", width: 100, render: (p) => p.companies },
];

export function ProductsPage() {
  const q = useProducts();
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<ProductRow | null>(null);
  const [adding, setAdding] = useState(false);

  const all = q.data?.products ?? [];
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((p) => p.name.toLowerCase().includes(needle)
      || p.kind.includes(needle));
  }, [all, search]);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Products"
        desc="Every catalogue kind is a product. It carries its models, its spaces, its permission map per tool, its reference files and its tool releases."
      />

      <StatGrid stats={[
        { k: "Products", v: all.length },
        { k: "With models", v: all.filter((p) => p.catalogue_versions > 0).length },
        { k: "With spaces", v: all.filter((p) => p.spaces > 0).length },
        { k: "Reference files", v: all.reduce((n, p) => n + p.reference_files, 0) },
      ]} />

      <div style={{ display: "flex", gap: 10, margin: "10px 0", alignItems: "center" }}>
        <SearchInput value={search} placeholder="Search products…"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} />
        {mayAuthor && (
          <button className={adding ? "btn ghost sm" : "btn teal sm"}
            style={{ marginLeft: "auto" }} onClick={() => setAdding(!adding)}>
            {adding ? "Cancel" : "Add product"}
          </button>
        )}
      </div>

      {adding && <AddProductRow onDone={(p) => { setAdding(false); setOpen(p); }} />}

      <DataTable rows={rows} cols={COLS} rowKey={(p) => p.kind}
        onRowClick={(p) => setOpen(p)}
        empty={<div className="panel panel-pad cap">
          {q.isLoading ? "Loading…" : "No products match."}
        </div>} />

      {open && <ProductDrawer product={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** What the table cannot carry: the things the counts are OF. */
function ProductDrawer({ product, onClose }: {
  product: ProductRow; onClose: () => void;
}) {
  const nav = useNavigate();
  const models = useModels(product.kind);
  const spaces = useSpaces(product.kind);

  const go = (to: string) => { onClose(); nav(to); };

  return (
    <Drawer onClose={onClose} header={
      <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <strong>{product.name}</strong>
        <code className="cap">{product.kind}</code>
        {product.is_asset && <Chip tone="ok">asset</Chip>}
        {product.is_connected && <Chip tone="info">connected</Chip>}
      </span>
    }>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 14 }}>
        <KV k="Abbreviation" v={product.abbr} mono />
        <KV k="Status" v={product.status} />
        <KV k="Companies holding it" v={product.companies} />
      </div>

      <Section title="Models" count={models.data?.models.length ?? 0}
        onOpen={() => go("/asset-catalog/models")} linkLabel="Asset Catalog">
        {(models.data?.models ?? []).map((m) => (
          <div key={m.model_id} style={{ display: "flex", gap: 8, padding: "2px 0" }}>
            <span style={{ fontFamily: "var(--mono)" }}>{m.name}</span>
            <span className="cap">
              {m.versions} version{m.versions === 1 ? "" : "s"}
            </span>
            {m.status !== "active" && <Chip tone="mute">{m.status}</Chip>}
          </div>
        ))}
        {/* The honest empty state. `cluster` carries 17 reference files and no
            model, because it came from Fabric's cluster_meter while Orbit's
            hardware is `cluster_c` — so "no models" is a real answer here,
            not a loading glitch. */}
        {models.data && models.data.models.length === 0 && (
          <span className="cap">
            No models in the catalogue yet — reference files can still be
            common to the product.
          </span>
        )}
      </Section>

      <Section title="Spaces" count={product.spaces}
        onOpen={() => go("/products/spaces")} linkLabel="Spaces">
        {(spaces.data?.spaces ?? []).map((s) => (
          <div key={s.space_id} style={{ display: "flex", gap: 8, padding: "2px 0" }}>
            <span style={{ fontFamily: "var(--mono)" }}>{s.slug}</span>
            <span>{s.name}</span>
            <span className="cap">{s.allocations} allocated</span>
            {s.archived && <Chip tone="mute">archived</Chip>}
          </div>
        ))}
      </Section>

      {/* THE SPEC EDITOR. A product with no fields offers an empty model
          form, which is why a new one stays DRAFT until this has something
          in it — and why "Add product" could not be just a button. */}
      <div style={{ marginBottom: 14 }}>
        <SpecEditor kind={product.kind} />
      </div>

      <Companies kind={product.kind} />

      <Section title="Permission maps" count={product.permission_maps}
        onOpen={() => go("/products/permissions")} linkLabel="Permission map" />
      <Section title="Reference files" count={product.reference_files}
        onOpen={() => go("/products/reference")} linkLabel="Reference files" />
      <Section title="Tool releases" count={product.tool_releases}
        onOpen={() => go("/products/tools")} linkLabel="Tool updates" />
    </Drawer>
  );
}

function Section({ title, count, onOpen, linkLabel, children }: {
  title: string; count: number; onOpen: () => void; linkLabel: string;
  children?: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <strong>{title}</strong>
        <Chip tone="mute">{count}</Chip>
        <button className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={onOpen}>
          Open {linkLabel}
        </button>
      </div>
      {children && <div style={{ marginTop: 6 }}>{children}</div>}
    </div>
  );
}


/** WHO MAY WORK WITH THIS PRODUCT — both halves, on one screen.
 *
 *  Dattatray: *"we need to allocate a product somewhere and then allocated a
 *  model somewhere else! That is UX issue!"*
 *
 *  He is right that it is one question and wrong that it is one mechanism.
 *  They answer different halves, and both survive:
 *
 *    ENTITLEMENT  may this company use the product at all
 *                 (`company_products`)
 *    REACH        which models within it they may BUILD WITH
 *                 (`catalog_model_visibility`, D-81)
 *
 *  Collapsing them would lose something real: a company can hold BMS and
 *  still be kept off the ASIL-D model another customer commissioned. So the
 *  fix is the UX one he asked for — one screen — not a schema merge.
 *
 *  EMPTY VISIBILITY MEANS EVERYONE, not nobody (D-81), which is why the
 *  control says so in words. It is the opposite of `firmware_compatibility`,
 *  where no rows means the build reaches nothing, and the two conventions
 *  live one join apart.
 */
function Companies({ kind }: { kind: string }) {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const q = useProductCompanies(kind);
  const qc = useQueryClient();

  const setHolders = useMutation({
    mutationFn: (ids: string[]) => products.setCompanies(kind, ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.companies(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
    },
  });
  const setVisibility = useMutation({
    mutationFn: (v: { versionId: string; ids: string[] }) =>
      products.setModelVisibility(v.versionId, v.ids),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.companies(kind) }),
  });

  const all = q.data?.companies ?? [];
  const holders = all.filter((c) => c.holds_product).map((c) => c.company_id);

  const toggleHolder = (id: string) => setHolders.mutate(
    holders.includes(id) ? holders.filter((h) => h !== id) : [...holders, id]);

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <strong>Who may work with it</strong>
        <Chip tone="mute">{holders.length} hold it</Chip>
      </div>

      <div className="cap" style={{ marginTop: 4 }}>
        Holding the product lets a company use it at all. Model visibility
        narrows which designs within it they may build with.
      </div>

      <div style={{ marginTop: 8 }}>
        {all.map((c) => (
          <label key={c.company_id}
            style={{ display: "flex", gap: 8, alignItems: "center", padding: "2px 0" }}>
            <input type="checkbox" checked={c.holds_product} disabled={!mayAuthor
              || setHolders.isPending} onChange={() => toggleHolder(c.company_id)} />
            <span>{c.name}</span>
            <span className="cap">{c.company_id}</span>
          </label>
        ))}
        {all.length === 0 && <span className="cap">No organisations yet.</span>}
      </div>

      {(q.data?.models.length ?? 0) > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="cap" style={{ marginBottom: 4 }}>
            Model visibility — empty means every company that holds the product
          </div>
          {(q.data?.models ?? []).map((m) => (
            <div key={m.version_id}
              style={{ display: "flex", gap: 8, alignItems: "center", padding: "2px 0" }}>
              <span style={{ fontFamily: "var(--mono)", minWidth: 200 }}>
                {m.model} v{m.version}
              </span>
              {m.visible_to.length === 0
                ? <Chip tone="ok">everyone</Chip>
                : <Chip tone="warn">{m.visible_to.join(", ")}</Chip>}
              {mayAuthor && m.visible_to.length > 0 && (
                <button className="btn ghost sm" disabled={setVisibility.isPending}
                  onClick={() => setVisibility.mutate({ versionId: m.version_id, ids: [] })}>
                  Open to everyone
                </button>
              )}
              {mayAuthor && m.visible_to.length === 0 && holders.length > 0 && (
                <button className="btn ghost sm" disabled={setVisibility.isPending}
                  onClick={() => setVisibility.mutate({
                    versionId: m.version_id, ids: holders })}>
                  Restrict to holders
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {(setHolders.isError || setVisibility.isError) && (
        <div className="panel panel-pad" style={{ marginTop: 8 }}>
          {((setHolders.error ?? setVisibility.error) as Error).message}
        </div>
      )}
    </div>
  );
}


/** Add a product — which creates a catalogue KIND (D-92).
 *
 *  It lands as DRAFT, because the id is only half of it: until the spec
 *  editor in the drawer has fields, adding a model of this product would
 *  show a form with nothing on it. Opening the drawer straight after
 *  creating is deliberate — the next step is right there rather than
 *  something to go and find.
 *
 *  THE ID IS PERMANENT. `catalog_models.kind`, `catalog_model_versions.kind`,
 *  `parts.kind`, `assets.asset_type` and every products table point at it,
 *  and a tool sends it. The field says so rather than letting somebody find
 *  out later.
 */
function AddProductRow({ onDone }: { onDone: (p: ProductRow) => void }) {
  const qc = useQueryClient();
  const existing = useProducts();
  const [kind, setKind] = useState("");
  const [label, setLabel] = useState("");
  const [abbr, setAbbr] = useState("");
  const [group, setGroup] = useState("");
  const [isAsset, setIsAsset] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // Offer the categories that exist, so the picker stays groupable rather
  // than growing a one-product category per typo.
  const groups = Array.from(
    new Set((existing.data?.products ?? []).map((p) => p.group)));

  const add = useMutation({
    mutationFn: () => products.create({
      kind, label, abbr, group, is_asset: isAsset, is_connected: isConnected,
    }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone(p as unknown as ProductRow);
    },
  });

  const idOk = /^[a-z][a-z0-9_]{1,15}$/.test(kind);

  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      <ScopeField label="Product id" hint="PERMANENT — tools send it; lower case">
        <SearchInput value={kind} autoFocus mono placeholder="bms_hv" style={{ width: 150 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setKind(e.target.value.toLowerCase().replace(/[\s-]+/g, "_"))} />
      </ScopeField>
      <ScopeField label="Name" hint="what a person reads">
        <SearchInput value={label} placeholder="High-voltage BMS"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)} />
      </ScopeField>
      <ScopeField label="Short code" hint="prefixes technical model names">
        <SearchInput value={abbr} mono placeholder="BMSH" style={{ width: 90 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setAbbr(e.target.value.toUpperCase())} />
      </ScopeField>
      <ScopeField label="Category" hint="groups the product picker">
        <Select value={group} onChange={(e) => setGroup(e.target.value)}>
          <option value="">Select…</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </Select>
      </ScopeField>
      <ScopeField label="Shape" hint="an asset is enrolled; the rest are fitted">
        <span style={{ display: "flex", gap: 10, height: 30, alignItems: "center" }}>
          <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <input type="checkbox" checked={isAsset}
              onChange={(e) => setIsAsset(e.target.checked)} /> asset
          </label>
          <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <input type="checkbox" checked={isConnected}
              onChange={(e) => setIsConnected(e.target.checked)} /> connected
          </label>
        </span>
      </ScopeField>
      <ScopeAction>
        <button className="btn teal sm"
          disabled={!idOk || !label.trim() || !abbr.trim() || !group || add.isPending}
          onClick={() => add.mutate()}>
          {add.isPending ? "Creating…" : "Create product"}
        </button>
      </ScopeAction>
      {add.isError && (
        <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
          {(add.error as Error).message}
        </div>
      )}
    </div>
  );
}
